import { useEffect } from 'react';

/**
 * Keep a group of layers at the very top of the map, above everything else.
 *
 * The `anchor-*` layers in gl-map-optimized pin a layer BELOW a fixed point,
 * which is the right tool for "stay under the vectors" but cannot express
 * "above everything": an anchor declared at map load sits below every layer
 * added afterwards, and react-map-gl appends a layer whenever its <Source>
 * mounts. So any overlay switched on later — a Sentinel buffer, the drill
 * holes, an ASTER raster — lands on top of a layer that was declared last.
 * Re-raising after anything else touches the style is the only thing that
 * holds.
 *
 * Groups are registered in one module-level registry rather than each caller
 * raising its own layers, because two callers racing on `styledata` would
 * settle in whichever order the events happened to arrive. One pass over the
 * whole registry, lowest `rank` first, is deterministic: rank 10 ends up under
 * rank 20, every time.
 *
 * @param key    stable identifier for this group
 * @param rank   higher wins — the group ends up nearer the top
 * @param ids    layer ids in draw order (must be a stable array reference)
 * @param map    the react-map-gl MapRef, or null before the map exists
 * @param active whether the group is currently on the map
 */
const groups = new Map();

function raiseAll(map) {
  const wanted = [];
  [...groups.values()]
    .sort((a, b) => a.rank - b.rank)
    .forEach((g) => g.ids.forEach((id) => { if (map.getLayer(id)) wanted.push(id); }));
  if (!wanted.length) return;

  // Bail when they are already the topmost layers, in order. moveLayer fires
  // `styledata`, which brings us straight back here — without this check that
  // is a loop that never settles.
  const order = map.getStyle().layers.map((l) => l.id);
  const tail = order.slice(-wanted.length);
  if (tail.length === wanted.length && tail.every((id, i) => id === wanted[i])) return;

  wanted.forEach((id) => map.moveLayer(id));
}

export function useTopLayers(key, rank, ids, map, active) {
  useEffect(() => {
    if (!map || !active) return undefined;
    groups.set(key, { rank, ids });

    // `styledata` arrives in bursts — a source swap can fire it several times
    // for one change — and each pass reads the whole style. One coalesced pass
    // per frame is enough, since nothing is painted in between anyway.
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; raiseAll(map); });
    };

    schedule();
    map.on('styledata', schedule);
    return () => {
      groups.delete(key);
      if (frame) cancelAnimationFrame(frame);
      map.off('styledata', schedule);
      // Whatever is left keeps its order; this group's layers are unmounting.
    };
  }, [key, rank, ids, map, active]);
}

export default useTopLayers;
